import { getStoredUtmSource } from './utm';

const CLICK_ID_KEY = "wl_click_id";
const STATIC_PIXEL_BASE = "https://pixel.whistleloop.com/static_pixel";
const DEFAULT_OFFER_ID = "1830";

// ─── Source Routing Helpers ───────────────────────────────────────────────────
// Returns the utm_source stored in localStorage (uppercased, trimmed).
const getStoredSource = () => getStoredUtmSource();

/**
 * Returns true when the lead came from Valueleaf / Whistleloop.
 * The plain "Value-Leaf" source is the Meta link, so it is not listed here.
 */
const VALUELEAF_SOURCES = ["VALUELEAF", "VALUE-LEAF_WHISTLE"];

export const isValueleafSource = () => {
  if (VALUELEAF_SOURCES.includes(getStoredSource())) return true;
  // The utm_* keys in localStorage get cleared; the session snapshot remembers the source.
  return VALUELEAF_SOURCES.includes((readSession()?.source || "").toUpperCase());
};

/**
 * Returns true when the lead came from Meta / Facebook.
 */
export const isMetaSource = () => {
  const src = getStoredSource();

  // The Meta campaign runs through the plain "Value-Leaf" link; the "Value-Leaf_whistle"
  // variant is the ValueLeaf postback link and stays out of Meta.
  if (src === "VALUE-LEAF") return true;

  // Distinct partner traffic should never be attributed to Meta
  if (
    src === "VALUELEAF" ||
    src === "VALUE-LEAF" ||
    src === "VALUE-LEAF_WHISTLE" ||
    src.includes("VALUE") ||
    src.includes("WHISTLE") ||
    src === "PROFUSE" ||
    src.includes("PROFUSE") ||
    src === "TRACKIER" ||
    src.includes("TRACKIER") ||
    src === "GOOGLE" ||
    src.includes("INTEXM")
  ) {
    return false;
  }

  let hasFbclid = false;
  try {
    if (typeof window !== 'undefined') {
      hasFbclid = Boolean(
        new URLSearchParams(window.location.search).get("fbclid") ||
        localStorage.getItem("fbclid")
      );
    }
  } catch (e) {}

  return (
    src === "META" ||
    src === "FACEBOOK" ||
    src === "FB" ||
    src.includes("META") ||
    src.includes("FACEBOOK") ||
    hasFbclid
  );
};

const OFFER_ID_KEY = "wl_offer_id";

// Captures click_id and, for marketing links like ?offerid=1830&goal_name=...,
// the offerid. A stored offerid marks the session as Whistleloop traffic.
export const captureWhistleloopClickId = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    const clickId = params.get("click_id") || params.get("utm_term");
    const offerId = params.get("offerid");
    const tracked = isValueleafSource() || Boolean(offerId);
    if (clickId && tracked) {
      localStorage.setItem(CLICK_ID_KEY, clickId);
    }
    if (offerId) {
      localStorage.setItem(OFFER_ID_KEY, offerId);
    }
    if (tracked) {
      // Snapshot now, not at first fire: later steps run on pages with no query string.
      writeSession({
        clickId,
        offerId,
        source: getStoredSource(),
      });
    }
  } catch (e) {}
};

/**
 * True for Valueleaf utm_source traffic OR sessions that landed via a
 * marketing link carrying ?offerid=...
 */
export const isWhistleloopSource = () => {
  if (isValueleafSource()) return true;
  try {
    return Boolean(
      localStorage.getItem(OFFER_ID_KEY) ||
      new URLSearchParams(window.location.search).get("offerid") ||
      readSession()
    );
  } catch (e) {
    return false;
  }
};

// The utm/click_id keys in localStorage are wiped whenever /apply-now is
// visited (eKYC/AA popup returns, back navigation). So once a pixel fires we
// snapshot the attribution in sessionStorage, which lives until logout
// (sessionStorage.clear()) and keeps later goals attributed.
const SESSION_KEY = "wl_session";

const readSession = () => {
  try {
    return JSON.parse(sessionStorage.getItem(SESSION_KEY)) || null;
  } catch (e) {
    return null;
  }
};

// Merges into the snapshot; an empty value never overwrites one already captured.
const writeSession = (fields) => {
  try {
    const next = { ...(readSession() || {}) };
    Object.keys(fields).forEach((key) => {
      if (fields[key]) next[key] = fields[key];
    });
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(next));
  } catch (e) {}
};

const getStoredOfferId = () => {
  try {
    return localStorage.getItem(OFFER_ID_KEY) || readSession()?.offerId || DEFAULT_OFFER_ID;
  } catch (e) {
    return DEFAULT_OFFER_ID;
  }
};

export const getWhistleloopClickId = () => {
  try {
    const stored = localStorage.getItem(CLICK_ID_KEY) || readSession()?.clickId;
    if (stored) return stored;
    // Fallback to the clickId Login.jsx saves from utm_term. setStorage() writes it
    // to sessionStorage, so look there first, then localStorage for older sessions.
    const legacy = sessionStorage.getItem("clickId") || localStorage.getItem("clickId");
    return legacy ? JSON.parse(legacy) : "";
  } catch (e) {
    return "";
  }
};

// Fires a Whistleloop static conversion pixel as a tracking beacon (new Image()) -
// no CORS needed, fire-and-forget, works even if the page navigates away right after.
//
// dedupeKey defaults to goalName (fires once per browser ever, via
// localStorage) - pass one that embeds an id (profile_id, lead_id) for goals
// that legitimately repeat across loan cycles.
export const fireWhistleloopPixel = (goalName, { revenue = 0, dedupeKey, offerId = getStoredOfferId() } = {}) => {
  if (!isWhistleloopSource()) return;
  const clickId = getWhistleloopClickId();
  writeSession({ clickId, offerId });
  // Without a click_id Whistleloop cannot attribute the conversion. Don't send it, and
  // don't mark it fired, so a later step that has the click_id can still send it.
  if (!clickId) return;
  const firedKey = `wl_fired_${dedupeKey || goalName}`;
  try {
    if (localStorage.getItem(firedKey)) return;
  } catch (e) {}

  let url = `${STATIC_PIXEL_BASE}?offerid=${encodeURIComponent(offerId)}&goal_name=${encodeURIComponent(goalName)}&click_id=${encodeURIComponent(clickId)}`;
  if (revenue) {
    url += `&revenue=${encodeURIComponent(revenue)}`;
  }

  try {
    const beacon = new Image();
    beacon.src = url;
  } catch (e) {}

  try {
    localStorage.setItem(firedKey, "1");
  } catch (e) {}
};

